import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  createMigrate,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { authReducer } from './slices/authSlice';
import { alertasLocalReducer } from './slices/alertasLocalSlice';
import { langReducer } from './slices/langSlice';
import { mercadoReducer } from './slices/mercadoSlice';
import { notificacionesLocalReducer } from './slices/notificacionesLocalSlice';
import { prefsReducer } from './slices/prefsSlice';
import { themeReducer } from './slices/themeSlice';
import { uiReducer } from './slices/uiSlice';

/**
 * Qué se persiste (localStorage, clave "pizarra-agro"):
 *  - auth: SOLO user + refreshToken (el access token vive en memoria),
 *  - theme, lang, prefs, alertasLocal, notificacionesLocal, mercado (snapshots ligeros).
 * ui NO se persiste. Ver docs/ARQUITECTURA.md §Estado.
 */
const authPersisted = persistReducer(
  { key: 'auth', storage, whitelist: ['user', 'refreshToken'] },
  authReducer,
);

const rootReducer = combineReducers({
  auth: authPersisted,
  theme: themeReducer,
  lang: langReducer,
  ui: uiReducer,
  prefs: prefsReducer,
  alertasLocal: alertasLocalReducer,
  notificacionesLocal: notificacionesLocalReducer,
  mercado: mercadoReducer,
});

const persistedReducer = persistReducer(
  {
    key: 'pizarra-agro',
    version: 1,
    storage,
    whitelist: ['theme', 'lang', 'prefs', 'alertasLocal', 'notificacionesLocal', 'mercado'],
    // Añade aquí migraciones cuando cambie la forma de un slice persistido.
    migrate: createMigrate({}, { debug: false }),
  },
  rootReducer,
);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefault) =>
    getDefault({
      serializableCheck: { ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER] },
    }),
  devTools: import.meta.env.DEV,
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

/** Acceso al store fuera de React (servicios, listeners). */
export const getStoreRef = () => store;

/** Store SIN persistencia ni localStorage, para pruebas (un store nuevo por test). */
export const createTestStore = (preloadedState?: Partial<RootState>) =>
  configureStore({ reducer: rootReducer, preloadedState: preloadedState as RootState | undefined });
export type TestStore = ReturnType<typeof createTestStore>;
